'use client'

import {
  formatTemplate,
  splitDuration,
  type TrackerCopy,
  type TrackerLocale,
  type TrackerState,
} from '@/lib/santa-tracker'
import { formatCountdownInline, formatLocalTime } from './format'

/**
 * «¿A qué hora llega a tu casa?».
 *
 * La franja se calcula con la zona horaria que **ya publica el navegador**;
 * nunca se pide la ubicación. Se enuncia como aproximación, no como promesa: la
 * página no puede saber la hora exacta y decirlo así es lo honesto con el
 * adulto sin romperle la magia al niño.
 */
export function TrackerEtaCard({
  copy,
  locale,
  state,
  utcOffsetHours,
  timeZoneName,
}: {
  copy: TrackerCopy
  locale: TrackerLocale
  state: TrackerState
  utcOffsetHours: number
  timeZoneName: string | null
}) {
  const alreadyPassed = state.visitorWindow.passed && state.phase !== 'preparing'
  const showRemaining = !state.visitorWindow.passed && state.countdownMs > 0

  return (
    <section
      aria-labelledby="tracker-eta-heading"
      className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-lg md:p-8"
    >
      <h2 id="tracker-eta-heading" className="font-christmas text-2xl font-semibold md:text-3xl">
        {copy.eta.heading}
      </h2>

      <p className="mt-3 text-lg text-white/90">
        {alreadyPassed
          ? copy.eta.passed
          : formatTemplate(copy.eta.body, {
              start: formatLocalTime(state.visitorWindow.startMs, utcOffsetHours, locale),
              end: formatLocalTime(state.visitorWindow.endMs, utcOffsetHours, locale),
            })}
      </p>

      {showRemaining && (
        <p className="mt-2 text-base tabular-nums text-white/80">
          {formatTemplate(copy.eta.remaining, {
            countdown: formatCountdownInline(splitDuration(state.countdownMs)),
          })}
        </p>
      )}

      <p className="mt-2 text-sm text-white/65">
        {timeZoneName
          ? formatTemplate(copy.eta.zone, { zone: timeZoneName })
          : copy.eta.unknownZone}
      </p>
      <p className="mt-3 text-xs text-white/55">{copy.eta.disclaimer}</p>
    </section>
  )
}
